/**
 * Where template budgets are kept.
 *
 * There is no budgets table or endpoint yet, so budgets live in this browser,
 * one record per organisation. Everything that reads or writes them goes
 * through this module, so moving them server-side later means replacing these
 * two functions and nothing else.
 *
 * The record is versioned and every read is validated: storage is shared with
 * every other tab and every older build of the app, and a malformed record
 * must come back as "no budgets", never as a page that throws.
 */
import { DEFAULT_ALERT_AT } from './budgets'

const PREFIX = 'xtractai-template-budgets'
export const STORE_VERSION = 1

export function storageKey(orgKey) {
  return `${PREFIX}:${orgKey || 'default'}`
}

export function emptyRecord() {
  return { version: STORE_VERSION, budgets: {}, overPlanAcknowledged: null }
}

/** Keep only well-formed budgets; anything else is dropped rather than trusted. */
export function sanitise(raw) {
  if (!raw || typeof raw !== 'object' || raw.version !== STORE_VERSION) return emptyRecord()
  const budgets = {}
  for (const [id, b] of Object.entries(raw.budgets || {})) {
    const amount = Number(b?.amount)
    if (!id || !Number.isFinite(amount) || amount <= 0) continue
    const alertAt = Number(b?.alertAt)
    budgets[String(id)] = {
      amount,
      alertAt: Number.isFinite(alertAt) && alertAt > 0 && alertAt <= 100 ? alertAt : DEFAULT_ALERT_AT,
      updatedAt: typeof b?.updatedAt === 'string' ? b.updatedAt : null,
    }
  }
  const ack = raw.overPlanAcknowledged
  return {
    version: STORE_VERSION,
    budgets,
    // An acknowledgement is for one specific total — budgets that move after
    // it was given are a new decision.
    overPlanAcknowledged: Number.isFinite(Number(ack)) && ack !== null ? Number(ack) : null,
  }
}

export function loadBudgets(orgKey) {
  try {
    const text = localStorage.getItem(storageKey(orgKey))
    return text ? sanitise(JSON.parse(text)) : emptyRecord()
  } catch {
    return emptyRecord()
  }
}

/** True when the write landed. A full or blocked storage is reported, not thrown. */
export function saveBudgets(orgKey, record) {
  try {
    localStorage.setItem(storageKey(orgKey), JSON.stringify(sanitise({ ...record, version: STORE_VERSION })))
    return true
  } catch {
    return false
  }
}
